/**
 * API Resource Node Component
 * Renders REST API resource in UML style with stereotype, attributes, and methods
 *
 * DUAL-MODE DESIGN:
 * - Export-safe foundation: Professional spacing, outlined badges, high contrast for PNG/SVG
 * - Interactive enhancements: Status code fills, hover states via @media (hover: hover)
 */

import { memo, useState, useEffect } from 'react'
import { NodeProps, NodeResizer, useUpdateNodeInternals } from '@xyflow/react'
import { NodeConnectionHandles } from './NodeConnectionHandles'
import type { APIResourceContent } from '../../types/schema'
import { ChevronDown, ChevronRight } from 'lucide-react'

interface APIResourceNodeData {
  label: string
  resourceContent?: APIResourceContent
  style?: {
    // Colors
    headerBgColor?: string    // Header background
    headerTextColor?: string  // Header text
    bgColor?: string          // Body background
    borderColor?: string
    textColor?: string
    // Border
    borderWidth?: number
    borderStyle?: string
    // Opacity
    fillOpacity?: number
    textOpacity?: number
    // Text formatting
    fontSize?: number
    fontWeight?: string
    fontFamily?: string
    textAlign?: string
    verticalAlign?: string
    lineHeight?: number
    textDecoration?: string
    // Text behavior
    wordWrap?: boolean
    textOverflow?: string
    textPadding?: number
    letterSpacing?: number
  }
  minWidth?: number
  minHeight?: number
  maxWidth?: number
  maxHeight?: number
}

// HTTP method colors - Clean UI with soft tinted backgrounds
const methodColors: Record<string, { bg: string, text: string }> = {
  GET: { bg: '#dbeafe', text: '#1e40af' },      // Soft blue
  POST: { bg: '#d1fae5', text: '#065f46' },     // Soft green
  PUT: { bg: '#fed7aa', text: '#92400e' },      // Soft orange
  DELETE: { bg: '#fee2e2', text: '#991b1b' },   // Soft red
  PATCH: { bg: '#f3e8ff', text: '#6b21a8' }     // Soft purple
}

// Common status codes for each HTTP method
const getStatusCodes = (method: string): Array<{code: string, label: string, color: string}> => {
  switch (method) {
    case 'GET':
      return [
        { code: '200', label: 'OK', color: '#10b981' },
        { code: '404', label: 'Not Found', color: '#f59e0b' }
      ]
    case 'POST':
      return [
        { code: '201', label: 'Created', color: '#10b981' },
        { code: '400', label: 'Bad Request', color: '#f59e0b' }
      ]
    case 'PUT':
    case 'PATCH':
      return [
        { code: '200', label: 'OK', color: '#10b981' },
        { code: '404', label: 'Not Found', color: '#f59e0b' }
      ]
    case 'DELETE':
      return [
        { code: '204', label: 'No Content', color: '#10b981' },
        { code: '404', label: 'Not Found', color: '#f59e0b' }
      ]
    default:
      return [{ code: '200', label: 'OK', color: '#10b981' }]
  }
}

function APIResourceNode({ id, data, selected, width, height }: NodeProps) {
  const nodeData = (data as unknown) as APIResourceNodeData
  const resourceContent = nodeData.resourceContent
  const updateNodeInternals = useUpdateNodeInternals()

  // Collapsible sections state
  const [attributesExpanded, setAttributesExpanded] = useState(true)
  const [methodsExpanded, setMethodsExpanded] = useState(true)

  // Force React Flow to measure the node after mount
  useEffect(() => {
    updateNodeInternals(id)
  }, [id, updateNodeInternals])

  // Toggle functions that update node dimensions
  const toggleAttributes = () => {
    setAttributesExpanded(prev => !prev)
    setTimeout(() => updateNodeInternals(id), 0)
  }

  const toggleMethods = () => {
    setMethodsExpanded(prev => !prev)
    setTimeout(() => updateNodeInternals(id), 0)
  }

  // Apply user-defined styles or defaults
  // Read from root level first (Properties Panel writes here), then fallback to nested style

  // Colors - Clean UI design with soft green tint
  const headerBg = (nodeData as any).headerBgColor !== undefined ? (nodeData as any).headerBgColor : (nodeData.style?.headerBgColor || '#f0fdf4')
  const headerTextColor = (nodeData as any).headerTextColor || nodeData.style?.headerTextColor || '#14532d'
  const bodyBg = (nodeData as any).bgColor !== undefined ? (nodeData as any).bgColor : (nodeData.style?.bgColor || '#ffffff')
  const textColor = (nodeData as any).textColor || nodeData.style?.textColor || '#1f2937'
  const borderColor = (nodeData as any).borderColor !== undefined ? (nodeData as any).borderColor : (nodeData.style?.borderColor || '#bbf7d0')

  // Border - Subtle 1px for clean design
  const borderWidth = (nodeData as any).borderWidth !== undefined ? (nodeData as any).borderWidth : (nodeData.style?.borderWidth !== undefined ? nodeData.style.borderWidth : 1)
  const borderStyle = (nodeData as any).borderStyle || nodeData.style?.borderStyle || 'solid'

  // Opacity
  const fillOpacity = (nodeData as any).fillOpacity !== undefined ? (nodeData as any).fillOpacity : (nodeData.style?.fillOpacity !== undefined ? nodeData.style.fillOpacity : 1)
  const textOpacity = (nodeData as any).textOpacity !== undefined ? (nodeData as any).textOpacity : (nodeData.style?.textOpacity !== undefined ? nodeData.style.textOpacity : 1)

  // Text formatting
  const fontSize = (nodeData as any).fontSize !== undefined ? (nodeData as any).fontSize : (nodeData.style?.fontSize || 12)
  const fontWeight = (nodeData as any).fontWeight || nodeData.style?.fontWeight || '400'
  const fontFamily = (nodeData as any).fontFamily || nodeData.style?.fontFamily || 'Inter, system-ui, sans-serif'
  const textAlign = (nodeData as any).textAlign || nodeData.style?.textAlign || 'left'
  const lineHeight = (nodeData as any).lineHeight !== undefined ? (nodeData as any).lineHeight : (nodeData.style?.lineHeight || 1.5)
  const textDecoration = (nodeData as any).textDecoration || nodeData.style?.textDecoration || 'none'

  // Text behavior
  const wordWrap = (nodeData as any).wordWrap !== undefined ? (nodeData as any).wordWrap : (nodeData.style?.wordWrap !== undefined ? nodeData.style.wordWrap : true)
  const textOverflow = (nodeData as any).textOverflow || nodeData.style?.textOverflow || 'ellipsis'
  const textPadding = (nodeData as any).textPadding !== undefined ? (nodeData as any).textPadding : (nodeData.style?.textPadding || 12)
  const letterSpacing = (nodeData as any).letterSpacing !== undefined ? (nodeData as any).letterSpacing : (nodeData.style?.letterSpacing || 0)

  // Use measured width/height from React Flow, or defaults
  const nodeWidth = width || 280
  const nodeHeight = height || 350

  // Fallback for basic node
  if (!resourceContent) {
    return (
      <div
        className="bg-white border-2 border-gray-300 rounded shadow-md flex flex-col"
        style={{ width: nodeWidth, height: nodeHeight, minWidth: 200, minHeight: 150 }}
      >
        <div className="bg-gray-100 px-3 py-2 border-b border-gray-300 font-semibold">
          {nodeData.label}
        </div>
        <div className="px-3 py-2 text-sm text-gray-500 italic">
          No resource content defined
        </div>
        <NodeConnectionHandles />
      </div>
    )
  }

  return (
    <>
      {/* Interactive Enhancements - Only apply when hover is supported (not during export) */}
      <style>
        {`
          /* Interactive polish - automatically stripped during PNG/SVG export */
          @media (hover: hover) {
            .method-row:hover {
              background-color: #f9fafb !important;
              transition: background-color 150ms ease;
            }

            /* Status codes: outlined in export, filled on hover */
            .status-code.status-success:hover {
              background-color: #d1fae5 !important;  /* Light green fill */
              transition: background-color 150ms ease;
            }

            .status-code.status-client-error:hover {
              background-color: #fef3c7 !important;  /* Light yellow fill */
              transition: background-color 150ms ease;
            }

            .status-code.status-server-error:hover {
              background-color: #fee2e2 !important;  /* Light red fill */
              transition: background-color 150ms ease;
            }

            .status-code:focus-visible {
              outline: 2px solid #3b82f6;
              outline-offset: 2px;
            }
          }
        `}
      </style>

      {selected && (
        <NodeResizer
          minWidth={200}
          minHeight={150}
        />
      )}

      <div
        className="rounded shadow-md flex flex-col"
        style={{
          width: nodeWidth,
          height: nodeHeight,
          borderWidth: borderWidth,
          borderStyle: borderStyle,
          borderColor: borderColor,
          backgroundColor: bodyBg,
          opacity: fillOpacity,
          overflow: 'hidden',
          fontFamily: fontFamily,
          color: textColor
        }}
      >
        {/* Stereotype - Fixed height */}
        <div
          className="text-center italic border-b border-gray-200 flex-shrink-0"
          style={{
            fontSize: fontSize - 2,
            fontFamily: fontFamily,
            padding: `${textPadding / 2}px ${textPadding}px`,
            letterSpacing: `${letterSpacing}px`,
            opacity: textOpacity * 0.7,
            overflow: textOverflow === 'visible' ? 'visible' : 'hidden',
            textOverflow: textOverflow === 'visible' ? undefined : (textOverflow as any),
            whiteSpace: textOverflow === 'ellipsis' ? 'nowrap' : (wordWrap ? 'normal' : 'nowrap')
          }}
          title={`<<${resourceContent.stereotype}>>`}
        >
          &lt;&lt;{resourceContent.stereotype}&gt;&gt;
        </div>

        {/* Resource Name - Fixed height, export-optimized spacing */}
        <div
          className="text-center flex-shrink-0"
          style={{
            backgroundColor: headerBg,
            color: headerTextColor,
            fontSize: fontSize + 2,
            fontWeight: fontWeight,
            fontFamily: fontFamily,
            padding: '16px',  // Fixed 16px for professional export spacing
            textAlign: textAlign as any,
            textDecoration: textDecoration,
            letterSpacing: `${letterSpacing}px`,
            lineHeight: lineHeight,
            overflow: textOverflow === 'visible' ? 'visible' : 'hidden',
            textOverflow: textOverflow === 'visible' ? undefined : (textOverflow as any),
            whiteSpace: textOverflow === 'ellipsis' ? 'nowrap' : (wordWrap ? 'normal' : 'nowrap')
          }}
          title={resourceContent.resourceName}
        >
          {resourceContent.resourceName}
        </div>

        {/* Divider */}
        <div className="border-t-2 flex-shrink-0" style={{ borderColor }} />

        {/* Scrollable Content Area */}
        <div className="flex-1 overflow-y-auto" style={{ overflowX: textOverflow === 'visible' ? 'visible' : 'hidden', minHeight: 0 }}>
          {/* Attributes Section */}
          {resourceContent.attributes && resourceContent.attributes.length > 0 && (
            <>
              <div
                className="flex items-center gap-1 px-3 py-1.5 cursor-pointer hover:bg-gray-50 border-b border-gray-200"
                onClick={toggleAttributes}
              >
                {attributesExpanded ? (
                  <ChevronDown className="w-3 h-3 text-gray-500" />
                ) : (
                  <ChevronRight className="w-3 h-3 text-gray-500" />
                )}
                <span className="text-xs font-semibold text-gray-600">
                  Attributes ({resourceContent.attributes.length})
                </span>
              </div>

              {attributesExpanded && (
                <div className="px-3 py-2 border-b border-gray-200">
                  {resourceContent.attributes.map((attr, index) => (
                    <div
                      key={index}
                      className="py-0.5"
                      style={{
                        fontSize,
                        fontFamily: 'JetBrains Mono, monospace',
                        overflow: textOverflow === 'visible' ? 'visible' : 'hidden',
                          textOverflow: textOverflow === 'visible' ? undefined : 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                      title={attr}
                    >
                      {attr}
                    </div>
                  ))}
                </div>
              )}

              {/* Divider */}
              <div className="border-t-2" style={{ borderColor }} />
            </>
          )}

          {/* Methods Section */}
          <div
            className="flex items-center gap-1 px-3 py-1.5 cursor-pointer hover:bg-gray-50 border-b border-gray-200"
            onClick={toggleMethods}
          >
            {methodsExpanded ? (
              <ChevronDown className="w-3 h-3 text-gray-500" />
            ) : (
              <ChevronRight className="w-3 h-3 text-gray-500" />
            )}
            <span className="text-xs font-semibold text-gray-600">
              Methods ({resourceContent.methods.length})
            </span>
          </div>

          {methodsExpanded && (
            <div>
              {resourceContent.methods.map((method, index) => {
                const statusCodes = getStatusCodes(method.type)
                return (
                  <div
                    key={index}
                    className="method-row border-b border-gray-100"
                    style={{
                      padding: '12px 16px',  // Export-safe padding
                      minHeight: '48px',  // Professional row height for methods
                      minWidth: 0
                    }}
                  >
                    <div className="flex items-center gap-2">
                      {/* Method Badge - Soft tinted background */}
                      <span
                        className="px-1.5 py-0.5 text-xs font-semibold rounded flex-shrink-0"
                        style={{
                          backgroundColor: methodColors[method.type]?.bg || '#f3f4f6',
                          color: methodColors[method.type]?.text || '#374151'
                        }}
                      >
                        {method.type}
                      </span>

                      {/* Method Signature */}
                      <span
                        className="font-medium flex-1"
                        style={{
                          fontSize,
                          fontFamily: 'JetBrains Mono, monospace',
                          overflow: textOverflow === 'visible' ? 'visible' : 'hidden',
                          textOverflow: textOverflow === 'visible' ? undefined : 'ellipsis',
                          whiteSpace: 'nowrap',
                          minWidth: 0
                        }}
                        title={method.signature}
                      >
                        {method.signature}
                      </span>
                    </div>

                    {/* Status Codes - Outlined for export, fills on hover */}
                    <div className="flex gap-2 mt-1 ml-12">
                      {statusCodes.map((status, idx) => (
                        <span
                          key={idx}
                          className={`status-code status-${status.code.startsWith('2') ? 'success' : status.code.startsWith('4') ? 'client-error' : 'server-error'} px-1.5 py-0.5 text-xs rounded`}
                          style={{
                            backgroundColor: '#ffffff',  // White background for export (saves ink)
                            border: `1px solid ${status.color}`,  // Subtle 1px border for clean design
                            color: status.color === '#10b981' ? '#065f46' : status.color === '#f59e0b' ? '#92400e' : '#991b1b',  // Dark text for readability
                            fontFamily: 'JetBrains Mono, monospace',
                            fontWeight: '600'
                          }}
                          title={`${status.code} ${status.label}`}
                          data-status-color={status.color}
                        >
                          {status.code} {status.label}
                        </span>
                      ))}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>

        <NodeConnectionHandles />
      </div>
    </>
  )
}

export default memo(APIResourceNode)
